'use client';

import { useFirebaseAuth } from '@/components/auth/FirebaseAuthProvider';
import {
  getDefaultTaskPresetForTool,
  getTaskPresetById,
  getTaskPresetFieldLabels,
  getTaskPresetsForTool,
  type AiWorkflowTaskPreset,
} from '@/lib/ai-workflow-presets';
import type { ToolEntry } from '@/lib/tools';
import DOMPurify from 'dompurify';
import { marked } from 'marked';
import Link from 'next/link';
import { useEffect, useMemo, useState } from 'react';

type AiWorkflowRuntimePanelProps = {
  entry: ToolEntry;
};

type RuntimeFieldKey =
  | 'brief'
  | 'sourceText'
  | 'context'
  | 'audience'
  | 'tone'
  | 'outputFormat'
  | 'extraInstructions';

type RuntimeFormState = Record<RuntimeFieldKey, string>;

type SelectOption = {
  value: string;
  label: string;
};

type AiRunPayload = {
  success?: boolean;
  data?: {
    output?: {
      text?: string;
      finishReason?: string | null;
    };
    usage?: {
      promptTokens?: number;
      completionTokens?: number;
      totalTokens?: number;
      costUsd?: number;
    };
    model?: string;
    requestId?: string | null;
    preset?: {
      title?: string;
      creditCost?: number;
    };
    presetId?: string | null;
    run?: unknown;
    aiCredits?: {
      remaining?: number;
      limit?: number;
    };
  };
  error?: unknown;
};

type AiRunsPayload = {
  success?: boolean;
  data?: {
    recent?: unknown;
    saved?: unknown;
  };
  error?: unknown;
};

type RuntimeFieldDefinition = {
  label: string;
  placeholder: string;
  help?: string;
  kind?: 'input' | 'textarea' | 'select';
  rows?: number;
  defaultValue?: string;
  options?: SelectOption[];
};

type RuntimeOutputSectionDefinition = {
  id: string;
  title: string;
  description: string;
  headings: string[];
};

type RuntimeConfig = {
  fieldOrder: RuntimeFieldKey[];
  fields: Record<RuntimeFieldKey, RuntimeFieldDefinition>;
  outputHeading: string;
  outputEmptyState: string;
  outputSections?: RuntimeOutputSectionDefinition[];
};

type RuntimeConfigInput = {
  fieldOrder: RuntimeFieldKey[];
  fieldOverrides?: Partial<Record<RuntimeFieldKey, Partial<RuntimeFieldDefinition>>>;
  outputHeading?: string;
  outputEmptyState?: string;
  outputSections?: RuntimeOutputSectionDefinition[];
};

type StoredRunRecord = {
  id: string;
  title: string;
  toolSlug: ToolEntry['slug'];
  createdAt: string;
  updatedAt: string;
  presetId: string | null;
  form: RuntimeFormState;
  outputText: string;
  model: string | null;
  finishReason: string | null;
  requestId: string | null;
  creditCost: number | null;
  isSaved: boolean;
  usage: {
    promptTokens: number;
    completionTokens: number;
    totalTokens: number;
    costUsd: number;
  } | null;
};

const AI_RUNTIME_HISTORY_LIMIT = 8;
const AI_RUNTIME_SAVED_LIMIT = 12;
const AI_RUNTIME_STORAGE_PREFIX = 'cleanor:ai-runtime';

const emptyFormState: RuntimeFormState = {
  brief: '',
  sourceText: '',
  context: '',
  audience: '',
  tone: '',
  outputFormat: '',
  extraInstructions: '',
};

const defaultFieldDefinitions: Record<RuntimeFieldKey, RuntimeFieldDefinition> = {
  brief: {
    label: 'Brief',
    placeholder: 'Describe the job this workflow should do.',
    help: 'Explain what should change, what should stay, and what a useful output looks like.',
    kind: 'textarea',
    rows: 5,
  },
  sourceText: {
    label: 'Source material',
    placeholder: 'Paste the source material for this workflow.',
    kind: 'textarea',
    rows: 8,
  },
  context: {
    label: 'Context',
    placeholder: 'Add channel, product, SEO, brand, or editorial context.',
    help: 'Use this for extra constraints that should shape the output but are not part of the source.',
    kind: 'textarea',
    rows: 4,
  },
  audience: {
    label: 'Audience',
    placeholder: 'general professional audience',
    defaultValue: 'general professional audience',
    kind: 'input',
  },
  tone: {
    label: 'Tone',
    placeholder: 'clear, practical',
    defaultValue: 'clear, practical',
    kind: 'input',
  },
  outputFormat: {
    label: 'Output format',
    placeholder: '',
    defaultValue: 'markdown',
    kind: 'select',
    options: [{ value: 'markdown', label: 'Markdown' }],
  },
  extraInstructions: {
    label: 'Extra instructions',
    placeholder: 'Add any output constraints here.',
    kind: 'textarea',
    rows: 3,
  },
};

function createRuntimeConfig(input: RuntimeConfigInput): RuntimeConfig {
  const fields = {
    brief: { ...defaultFieldDefinitions.brief, ...input.fieldOverrides?.brief },
    sourceText: { ...defaultFieldDefinitions.sourceText, ...input.fieldOverrides?.sourceText },
    context: { ...defaultFieldDefinitions.context, ...input.fieldOverrides?.context },
    audience: { ...defaultFieldDefinitions.audience, ...input.fieldOverrides?.audience },
    tone: { ...defaultFieldDefinitions.tone, ...input.fieldOverrides?.tone },
    outputFormat: {
      ...defaultFieldDefinitions.outputFormat,
      ...input.fieldOverrides?.outputFormat,
    },
    extraInstructions: {
      ...defaultFieldDefinitions.extraInstructions,
      ...input.fieldOverrides?.extraInstructions,
    },
  } satisfies Record<RuntimeFieldKey, RuntimeFieldDefinition>;

  return {
    fieldOrder: input.fieldOrder,
    fields,
    outputHeading: input.outputHeading ?? 'Latest output',
    outputEmptyState:
      input.outputEmptyState ?? 'Run the workflow to generate a first result for this page.',
    outputSections: input.outputSections ?? [],
  };
}

const runtimeConfigBySlug: Partial<Record<ToolEntry['slug'], RuntimeConfig>> = {
  'ai-story-generator': createRuntimeConfig({
    fieldOrder: ['brief', 'audience', 'tone', 'outputFormat', 'extraInstructions'],
    fieldOverrides: {
      brief: {
        label: 'Story prompt',
        placeholder: 'A lighthouse keeper who finds a message in a bottle.',
        help: 'A one-line premise, a genre, or a couple of characters is enough.',
      },
      audience: {
        label: 'Audience',
        defaultValue: 'general readers',
        placeholder: 'general readers (try "children ages 4-8")',
      },
      tone: {
        defaultValue: 'vivid, engaging',
        placeholder: 'vivid, engaging',
      },
      outputFormat: {
        label: 'Output',
        defaultValue: 'short story',
        options: [
          { value: 'short story', label: 'Short story' },
          { value: 'plot outline', label: 'Plot outline' },
          { value: 'scene draft', label: 'Scene draft' },
        ],
      },
      extraInstructions: {
        placeholder: 'Set the length, setting, or characters to include.',
      },
    },
    outputHeading: 'Story',
    outputEmptyState: 'Your story will appear here after the first run.',
    outputSections: [
      {
        id: 'story',
        title: 'Story',
        description: 'The generated short story.',
        headings: ['Story', 'Title'],
      },
    ],
  }),
  'ai-paraphrasing-tool': createRuntimeConfig({
    fieldOrder: ['sourceText', 'brief', 'outputFormat', 'extraInstructions'],
    fieldOverrides: {
      sourceText: {
        label: 'Text to paraphrase',
        placeholder: 'Paste the sentence or paragraph you want reworded.',
      },
      brief: {
        label: 'Instruction',
        defaultValue: 'Paraphrase the text, keeping the meaning and changing the wording.',
        help: 'Tweak only if you need a specific angle; the mode below controls the style.',
      },
      outputFormat: {
        label: 'Mode',
        defaultValue: 'standard',
        options: [
          { value: 'standard', label: 'Standard' },
          { value: 'fluent', label: 'Fluent' },
          { value: 'formal', label: 'Formal' },
          { value: 'simple', label: 'Simple' },
          { value: 'shorten', label: 'Shorten' },
        ],
      },
      extraInstructions: {
        placeholder: 'Do not add new information. Return only the rewritten text.',
      },
    },
    outputHeading: 'Paraphrased text',
    outputEmptyState: 'The rewritten text will appear here after the first run.',
    outputSections: [],
  }),
  'ai-summarizer': createRuntimeConfig({
    fieldOrder: ['sourceText', 'brief', 'outputFormat', 'extraInstructions'],
    fieldOverrides: {
      sourceText: {
        label: 'Text to summarize',
        placeholder: 'Paste the article, report, transcript, or notes to summarize.',
      },
      brief: {
        label: 'Instruction',
        defaultValue: 'Summarize the text below, keeping the key points and facts.',
        help: 'Tweak only if you want a specific focus; the format below controls the shape.',
      },
      outputFormat: {
        label: 'Format',
        defaultValue: 'tl;dr + bullets',
        options: [
          { value: 'tl;dr + bullets', label: 'TL;DR + bullets' },
          { value: 'bullet key points', label: 'Bullet key points' },
          { value: 'executive summary', label: 'Executive summary' },
          { value: 'key takeaways', label: 'Key takeaways' },
        ],
      },
      extraInstructions: {
        placeholder: 'Summarize only what is in the source; do not add new facts.',
      },
    },
    outputHeading: 'Summary',
    outputEmptyState: 'The summary will appear here after the first run.',
    outputSections: [],
  }),
  'article-rewriter-refresh': createRuntimeConfig({
    fieldOrder: ['brief', 'sourceText', 'audience', 'tone', 'outputFormat', 'extraInstructions'],
    fieldOverrides: {
      brief: {
        placeholder:
          'Rewrite this article for founders, tighten the intro, and keep the tone direct.',
        help: 'Focus the rewrite on angle shifts, structure, and what should remain intact.',
      },
      sourceText: {
        label: 'Source article',
        placeholder: 'Paste the article, draft, or excerpt you want to rewrite.',
      },
      audience: {
        defaultValue: 'startup founders',
        placeholder: 'startup founders',
      },
      tone: {
        defaultValue: 'direct, clear, editorial',
        placeholder: 'direct, clear, editorial',
      },
      outputFormat: {
        options: [
          { value: 'markdown', label: 'Markdown' },
          { value: 'plain text', label: 'Plain text' },
          { value: 'outline + draft', label: 'Outline + draft' },
        ],
      },
      extraInstructions: {
        placeholder: 'Keep it under 900 words.',
      },
    },
    outputHeading: 'Rewrite output',
    outputEmptyState: 'Your revised article, structure, or draft handoff will appear here.',
    outputSections: [
      {
        id: 'revised-draft',
        title: 'Revised draft',
        description: 'The main rewritten version that should be ready for review or publishing.',
        headings: ['Revised Draft'],
      },
      {
        id: 'change-summary',
        title: 'Change summary',
        description: 'A compact summary of what changed and why.',
        headings: ['Change Summary', 'Editorial Notes'],
      },
    ],
  }),
  'essay-writer-assistant': createRuntimeConfig({
    fieldOrder: ['brief', 'sourceText', 'context', 'tone', 'outputFormat', 'extraInstructions'],
    fieldOverrides: {
      brief: {
        placeholder:
          'Build an essay outline, main arguments, and a solid first draft from this topic while keeping the reasoning clear and structured.',
        help: 'Explain the essay goal, the thesis or angle, and how complete the first draft should be.',
      },
      sourceText: {
        label: 'Topic and notes',
        placeholder:
          'Paste the essay topic, thesis, notes, references, constraints, or any source material you want the draft to use.',
      },
      context: {
        label: 'Assignment context',
        placeholder:
          'Add the word count, assignment framing, reading list, or structural requirements.',
      },
      tone: {
        defaultValue: 'clear, structured, thoughtful',
        placeholder: 'clear, structured, thoughtful',
      },
      outputFormat: {
        defaultValue: 'outline + draft',
        options: [
          { value: 'outline + draft', label: 'Outline + draft' },
          { value: 'markdown', label: 'Markdown' },
          { value: 'arguments + draft', label: 'Arguments + draft' },
        ],
      },
      extraInstructions: {
        placeholder: 'Keep the argument balanced, make the outline explicit, and avoid filler.',
      },
    },
    outputHeading: 'Essay draft',
    outputEmptyState:
      'The outline, arguments, and essay draft will appear here after the first run.',
    outputSections: [
      {
        id: 'thesis-direction',
        title: 'Thesis and direction',
        description: 'The essay angle, thesis, or core line of argument.',
        headings: ['Thesis and Direction', 'Thesis'],
      },
      {
        id: 'outline',
        title: 'Outline',
        description: 'A section-by-section structure for the essay.',
        headings: ['Outline', 'Essay Outline'],
      },
      {
        id: 'draft',
        title: 'Draft',
        description: 'The working first draft based on the selected task.',
        headings: ['Draft', 'Working Draft'],
      },
      {
        id: 'revision-notes',
        title: 'Revision notes',
        description: 'Short notes on what to refine next.',
        headings: ['Revision Notes', 'Revision Checklist'],
      },
    ],
  }),
  'social-post-studio': createRuntimeConfig({
    fieldOrder: ['brief', 'sourceText', 'context', 'audience', 'tone', 'outputFormat'],
    fieldOverrides: {
      brief: {
        placeholder:
          'Turn this launch update into platform-specific posts for LinkedIn, X, Instagram, Threads, and Facebook.',
        help: 'Define which platforms matter and which message must stay stable across them.',
      },
      sourceText: {
        label: 'Source material',
        placeholder: 'Paste the launch note, article excerpt, offer, or URL notes to repurpose.',
      },
      context: {
        label: 'Channels and goals',
        placeholder:
          'Add which channels matter, what CTA to keep, and what the launch or campaign is trying to do.',
      },
      audience: {
        defaultValue: 'startup founders, operators, and marketers',
        placeholder: 'startup founders, operators, and marketers',
      },
      tone: {
        defaultValue: 'confident, concise, launch-ready',
        placeholder: 'confident, concise, launch-ready',
      },
      outputFormat: {
        defaultValue: 'platform post pack',
        options: [
          { value: 'platform post pack', label: 'Platform post pack' },
          { value: 'markdown', label: 'Markdown' },
          { value: 'short captions', label: 'Short captions' },
        ],
      },
    },
    outputHeading: 'Social output pack',
    outputEmptyState: 'The platform-specific post pack will appear here after the first run.',
    outputSections: [
      {
        id: 'linkedin',
        title: 'LinkedIn',
        description: 'A platform-shaped LinkedIn post.',
        headings: ['LinkedIn', 'LinkedIn Post'],
      },
      {
        id: 'x',
        title: 'X',
        description: 'A tighter short-form post or thread opener for X.',
        headings: ['X', 'X Post', 'Twitter', 'Twitter Post'],
      },
      {
        id: 'instagram',
        title: 'Instagram',
        description: 'A caption-ready version for Instagram.',
        headings: ['Instagram', 'Instagram Caption'],
      },
      {
        id: 'threads',
        title: 'Threads',
        description: 'A Threads-native version with lighter structure.',
        headings: ['Threads', 'Threads Post'],
      },
      {
        id: 'facebook',
        title: 'Facebook',
        description: 'A Facebook-ready version with a clearer CTA.',
        headings: ['Facebook', 'Facebook Post'],
      },
    ],
  }),
  'content-repurposing-studio': createRuntimeConfig({
    fieldOrder: ['brief', 'sourceText', 'context', 'audience', 'tone', 'outputFormat'],
    fieldOverrides: {
      brief: {
        placeholder:
          'Turn this article into a reusable content pack with a summary, LinkedIn post, X thread, email intro, and short hooks.',
        help: 'Use the brief to define which derivative assets matter and what should stay consistent.',
      },
      sourceText: {
        label: 'Source content',
        placeholder:
          'Paste the article, blog draft, launch note, transcript excerpt, or source notes you want to repurpose.',
      },
      context: {
        label: 'Repurpose targets',
        placeholder:
          'List the target formats, channels, or distribution goals for this content pack.',
      },
      audience: {
        defaultValue: 'founders, operators, marketers, and newsletter readers',
        placeholder: 'founders, operators, marketers, and newsletter readers',
      },
      tone: {
        defaultValue: 'clear, adaptable, channel-aware',
        placeholder: 'clear, adaptable, channel-aware',
      },
      outputFormat: {
        defaultValue: 'content pack',
        options: [
          { value: 'content pack', label: 'Content pack' },
          { value: 'markdown', label: 'Markdown' },
          { value: 'summary + posts + email', label: 'Summary + posts + email' },
        ],
      },
      extraInstructions: {
        placeholder:
          'Keep the thread punchy, make the email intro concise, and avoid repeating the same hook everywhere.',
      },
    },
    outputHeading: 'Repurposed content pack',
    outputEmptyState: 'The summary, posts, hooks, and email-ready outputs will appear here.',
    outputSections: [
      {
        id: 'summary',
        title: 'Summary',
        description: 'The distilled high-level summary of the source material.',
        headings: ['Summary', 'Executive Summary'],
      },
      {
        id: 'linkedin-post',
        title: 'LinkedIn post',
        description: 'A LinkedIn-ready version derived from the source.',
        headings: ['LinkedIn Post', 'LinkedIn'],
      },
      {
        id: 'x-thread',
        title: 'X thread',
        description: 'A short-form thread or thread starter.',
        headings: ['X Thread', 'Thread', 'Twitter Thread'],
      },
      {
        id: 'email-intro',
        title: 'Email intro',
        description: 'A compact email intro or newsletter opening.',
        headings: ['Email Intro', 'Newsletter Intro'],
      },
      {
        id: 'hook-bank',
        title: 'Hook bank',
        description: 'Reusable hooks for other distribution surfaces.',
        headings: ['Hook Bank', 'Hooks'],
      },
    ],
  }),
  'tone-voice-rewriter': createRuntimeConfig({
    fieldOrder: ['brief', 'sourceText', 'context', 'tone', 'outputFormat', 'extraInstructions'],
    fieldOverrides: {
      brief: {
        placeholder:
          'Rewrite this draft in a sharper founder voice, keep the core meaning, and make the language feel more direct and confident.',
        help: 'Use the brief to define the target voice and what should remain unchanged.',
      },
      sourceText: {
        label: 'Source copy',
        placeholder:
          'Paste the copy, landing page section, email draft, article excerpt, or notes you want rewritten.',
      },
      context: {
        label: 'Brand or voice notes',
        placeholder: 'Add examples of the desired voice, banned phrases, or brand style notes.',
      },
      tone: {
        label: 'Target voice',
        defaultValue: 'direct, confident, founder-led',
        placeholder: 'direct, confident, founder-led',
      },
      outputFormat: {
        defaultValue: 'rewritten copy',
        options: [
          { value: 'rewritten copy', label: 'Rewritten copy' },
          { value: 'markdown', label: 'Markdown' },
          { value: 'multiple voice variants', label: 'Multiple voice variants' },
        ],
      },
      extraInstructions: {
        placeholder: 'Keep it concise, remove fluff, and avoid sounding generic or overhyped.',
      },
    },
    outputHeading: 'Rewritten copy',
    outputEmptyState: 'The rewritten copy will appear here, along with any variants you request.',
    outputSections: [
      {
        id: 'rewritten-copy',
        title: 'Rewritten copy',
        description: 'The main rewritten version in the target voice.',
        headings: ['Rewritten Copy', 'Primary Rewrite'],
      },
      {
        id: 'what-changed',
        title: 'What changed',
        description: 'Short notes on voice or messaging shifts.',
        headings: ['What Changed', 'Voice Notes'],
      },
      {
        id: 'variants',
        title: 'Variants',
        description: 'Additional variants when the task asks for more than one direction.',
        headings: ['Variants', 'Optional Variants', 'Voice Variants'],
      },
    ],
  }),
  'product-to-social-campaign': createRuntimeConfig({
    fieldOrder: ['brief', 'sourceText', 'context', 'audience', 'tone', 'outputFormat'],
    fieldOverrides: {
      brief: {
        placeholder:
          'Turn this product info into a launch-ready campaign pack with a main post, promo angle, story captions, hooks, and CTA variants.',
        help: 'Use the brief to define the campaign moment and what kind of social pack should come back.',
      },
      sourceText: {
        label: 'Product and campaign context',
        placeholder:
          'Paste product facts, benefits, launch notes, offer details, target audience, objections, and any existing copy.',
      },
      context: {
        label: 'Offer and campaign window',
        placeholder:
          'Add discount details, promo timing, channel priorities, and what angle should lead the campaign.',
      },
      audience: {
        defaultValue: 'shoppers comparing options on social and marketplace channels',
        placeholder: 'shoppers comparing options on social and marketplace channels',
      },
      tone: {
        defaultValue: 'clear, persuasive, campaign-ready',
        placeholder: 'clear, persuasive, campaign-ready',
      },
      outputFormat: {
        defaultValue: 'campaign pack',
        options: [
          { value: 'campaign pack', label: 'Campaign pack' },
          { value: 'markdown', label: 'Markdown' },
          { value: 'posts + hooks + ctas', label: 'Posts + hooks + CTAs' },
        ],
      },
      extraInstructions: {
        placeholder:
          'Keep hooks short, make CTAs specific, and vary the angles across the outputs.',
      },
    },
    outputHeading: 'Campaign output pack',
    outputEmptyState: 'The product-led campaign pack will appear here after the first run.',
    outputSections: [
      {
        id: 'main-post',
        title: 'Main campaign post',
        description: 'The lead post for the campaign.',
        headings: ['Main Campaign Post', 'Main Post', 'Launch Post'],
      },
      {
        id: 'supporting-posts',
        title: 'Supporting posts',
        description: 'Additional posts that extend the campaign.',
        headings: ['Supporting Posts', 'Support Posts'],
      },
      {
        id: 'hook-variants',
        title: 'Hook variants',
        description: 'Short alternative openings for testing.',
        headings: ['Hook Variants', 'Hooks'],
      },
      {
        id: 'cta-variants',
        title: 'CTA variants',
        description: 'CTA options matched to the offer and audience.',
        headings: ['CTA Variants', 'CTAs'],
      },
    ],
  }),
  'product-listing-writer': createRuntimeConfig({
    fieldOrder: [
      'brief',
      'sourceText',
      'context',
      'audience',
      'tone',
      'outputFormat',
      'extraInstructions',
    ],
    fieldOverrides: {
      brief: {
        placeholder:
          'Write a conversion-focused product listing with a stronger title, five bullets, and a concise description for Shopify.',
        help: 'Use the brief to define the marketplace, the listing goal, and what the copy should emphasize.',
      },
      sourceText: {
        label: 'Product details',
        placeholder:
          'Paste the product name, features, specs, benefits, audience notes, price cues, and any existing copy.',
      },
      context: {
        label: 'Marketplace and positioning',
        placeholder:
          'Add the storefront, listing rules, brand angle, or competitive positioning that should shape the copy.',
      },
      audience: {
        label: 'Buyer profile',
        defaultValue: 'online shoppers comparing similar products',
        placeholder: 'online shoppers comparing similar products',
      },
      tone: {
        defaultValue: 'clear, benefit-led, conversion-focused',
        placeholder: 'clear, benefit-led, conversion-focused',
      },
      outputFormat: {
        defaultValue: 'listing pack',
        options: [
          { value: 'listing pack', label: 'Listing pack' },
          { value: 'markdown', label: 'Markdown' },
          { value: 'title + bullets + description', label: 'Title + bullets + description' },
        ],
      },
      extraInstructions: {
        placeholder:
          'Avoid hype, keep bullets scannable, and make the description easy to publish.',
      },
    },
    outputHeading: 'Listing copy output',
    outputEmptyState: 'The title, bullets, description, and conversion copy will appear here.',
    outputSections: [
      {
        id: 'product-title',
        title: 'Product title',
        description: 'The main listing title.',
        headings: ['Product Title', 'Title'],
      },
      {
        id: 'bullet-points',
        title: 'Bullet points',
        description: 'The primary scannable product bullets.',
        headings: ['Bullet Points', 'Bullets', 'Key Bullets'],
      },
      {
        id: 'product-description',
        title: 'Product description',
        description: 'The longer listing description.',
        headings: ['Product Description', 'Description'],
      },
      {
        id: 'notes',
        title: 'Notes',
        description: 'Optional notes or warnings when the source is thin.',
        headings: ['Notes', 'Publishing Notes'],
      },
    ],
  }),
  'seo-brief-to-draft': createRuntimeConfig({
    fieldOrder: [
      'brief',
      'sourceText',
      'context',
      'audience',
      'tone',
      'outputFormat',
      'extraInstructions',
    ],
    fieldOverrides: {
      brief: {
        placeholder:
          'Turn this keyword into a practical SEO brief with search intent, heading structure, coverage notes, and a working first draft.',
        help: 'Use the brief to define the topic, search intent, and how far the workflow should go from brief into draft.',
      },
      sourceText: {
        label: 'Topic context',
        placeholder:
          'Paste the keyword, topic notes, competing angles, product context, internal notes, or an existing outline.',
      },
      context: {
        label: 'SEO context',
        placeholder:
          'Add target SERP angle, internal linking intent, stage of funnel, or editorial direction.',
      },
      audience: {
        defaultValue: 'search-driven readers comparing options and looking for practical answers',
        placeholder: 'search-driven readers comparing options and looking for practical answers',
      },
      tone: {
        defaultValue: 'clear, authoritative, search-focused',
        placeholder: 'clear, authoritative, search-focused',
      },
      outputFormat: {
        defaultValue: 'seo brief + draft',
        options: [
          { value: 'seo brief + draft', label: 'SEO brief + draft' },
          { value: 'markdown', label: 'Markdown' },
          { value: 'outline + notes + draft', label: 'Outline + notes + draft' },
        ],
      },
      extraInstructions: {
        placeholder: 'Prioritize informational intent, keep headings scannable, and avoid filler.',
      },
    },
    outputHeading: 'SEO brief and draft',
    outputEmptyState: 'The SEO brief, heading structure, and first draft will appear here.',
    outputSections: [
      {
        id: 'search-intent',
        title: 'Search intent',
        description: 'The core search intent and audience framing.',
        headings: ['Search Intent', 'Intent'],
      },
      {
        id: 'outline',
        title: 'Outline',
        description: 'The proposed heading structure.',
        headings: ['Outline', 'Heading Outline'],
      },
      {
        id: 'coverage-notes',
        title: 'Coverage notes',
        description: 'What the page should cover and why.',
        headings: ['Coverage Notes', 'Coverage'],
      },
      {
        id: 'working-draft',
        title: 'Working draft',
        description: 'The first draft content or draft sections.',
        headings: ['Working Draft', 'Draft'],
      },
    ],
  }),
};

function getApiErrorMessage(error: unknown) {
  if (typeof error === 'string') {
    return error;
  }

  if (
    error &&
    typeof error === 'object' &&
    'message' in error &&
    typeof error.message === 'string'
  ) {
    return error.message;
  }

  return 'AI run did not complete. Please try again.';
}

function createSafeMarkdownRenderer() {
  const renderer = new marked.Renderer();

  renderer.html = () => '';
  renderer.link = ({ href, title, text }) => {
    const safeHref = href?.trim() || '#';
    const safeTitle = title ? ` title="${title.replace(/"/g, '&quot;')}"` : '';
    const rel = safeHref.startsWith('http') ? ' rel="noreferrer"' : '';
    const target = safeHref.startsWith('http') ? ' target="_blank"' : '';

    return `<a href="${safeHref.replace(/"/g, '&quot;')}"${safeTitle}${rel}${target}>${text}</a>`;
  };

  renderer.image = ({ href, text, title }) => {
    const safeHref = href?.trim();

    if (!safeHref) {
      return '';
    }

    const safeTitle = title ? ` title="${title.replace(/"/g, '&quot;')}"` : '';
    return `<img src="${safeHref.replace(/"/g, '&quot;')}" alt="${(text || '').replace(/"/g, '&quot;')}"${safeTitle} />`;
  };

  return renderer;
}

function renderRuntimeMarkdownToHtml(markdown: string) {
  if (!markdown.trim()) {
    return '';
  }

  const rawHtml = marked.parse(markdown, {
    gfm: true,
    breaks: false,
    renderer: createSafeMarkdownRenderer(),
  }) as string;

  return DOMPurify.sanitize(rawHtml, {
    USE_PROFILES: { html: true },
  });
}

function normalizeHeadingLabel(value: string) {
  return value
    .toLowerCase()
    .replace(/[^\p{L}\p{N}\s]/gu, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function extractStructuredOutputSections(
  outputText: string,
  sectionDefinitions: RuntimeOutputSectionDefinition[],
) {
  if (!outputText.trim() || !sectionDefinitions.length) {
    return [] as Array<{
      id: string;
      title: string;
      description: string;
      heading: string;
      markdown: string;
    }>;
  }

  const lines = outputText.split('\n');
  const blocks: Array<{ heading: string; markdown: string }> = [];
  let currentHeading: string | null = null;
  let currentContent: string[] = [];

  function pushBlock() {
    if (!currentHeading) {
      return;
    }

    const markdown = currentContent.join('\n').trim();

    blocks.push({
      heading: currentHeading,
      markdown,
    });
  }

  for (const line of lines) {
    const match = line.match(/^(#{1,6})\s+(.+?)\s*$/);

    if (match) {
      pushBlock();
      currentHeading = match[2].trim();
      currentContent = [];
      continue;
    }

    if (currentHeading) {
      currentContent.push(line);
    }
  }

  pushBlock();

  return sectionDefinitions
    .map((definition) => {
      const match = blocks.find((block) =>
        definition.headings.some(
          (heading) => normalizeHeadingLabel(block.heading) === normalizeHeadingLabel(heading),
        ),
      );

      if (!match || !match.markdown) {
        return null;
      }

      return {
        id: definition.id,
        title: definition.title,
        description: definition.description,
        heading: match.heading,
        markdown: match.markdown,
      };
    })
    .filter(
      (
        section,
      ): section is {
        id: string;
        title: string;
        description: string;
        heading: string;
        markdown: string;
      } => Boolean(section),
    );
}

function normalizeIdentity(value: string | null | undefined) {
  if (typeof value !== 'string') {
    return null;
  }

  const normalized = value.trim().toLowerCase();
  return normalized ? normalized : null;
}

function getLocalRuntimeStorageKey(
  kind: 'history' | 'saved',
  toolSlug: ToolEntry['slug'],
  user: { uid: string; email: string | null },
) {
  const normalizedEmail = normalizeIdentity(user.email);
  const identity = normalizedEmail ? `email:${normalizedEmail}` : `uid:${user.uid}`;

  return `${AI_RUNTIME_STORAGE_PREFIX}:${kind}:${identity}:${toolSlug}`;
}

function coerceString(value: unknown) {
  return typeof value === 'string' ? value : '';
}

function coerceUsage(value: unknown) {
  if (!value || typeof value !== 'object') {
    return null;
  }

  const usage = value as {
    promptTokens?: unknown;
    completionTokens?: unknown;
    totalTokens?: unknown;
    costUsd?: unknown;
  };

  const promptTokens = Number(usage.promptTokens);
  const completionTokens = Number(usage.completionTokens);
  const totalTokens = Number(usage.totalTokens);
  const costUsd = Number(usage.costUsd);

  if (
    !Number.isFinite(promptTokens) ||
    !Number.isFinite(completionTokens) ||
    !Number.isFinite(totalTokens) ||
    !Number.isFinite(costUsd)
  ) {
    return null;
  }

  return {
    promptTokens: Math.max(0, Math.trunc(promptTokens)),
    completionTokens: Math.max(0, Math.trunc(completionTokens)),
    totalTokens: Math.max(0, Math.trunc(totalTokens)),
    costUsd: Math.max(0, costUsd),
  };
}

function normalizeStoredRunRecord(value: unknown): StoredRunRecord | null {
  if (!value || typeof value !== 'object') {
    return null;
  }

  const record = value as {
    id?: unknown;
    title?: unknown;
    toolSlug?: unknown;
    createdAt?: unknown;
    updatedAt?: unknown;
    presetId?: unknown;
    form?: Partial<Record<RuntimeFieldKey, unknown>>;
    outputText?: unknown;
    model?: unknown;
    finishReason?: unknown;
    requestId?: unknown;
    creditCost?: unknown;
    isSaved?: unknown;
    usage?: unknown;
  };

  const id = coerceString(record.id);
  const title = coerceString(record.title);
  const createdAt = coerceString(record.createdAt);
  const updatedAt = coerceString(record.updatedAt) || createdAt;
  const outputText = coerceString(record.outputText);

  if (!id || !title || !createdAt || !outputText) {
    return null;
  }

  const form = {
    brief: coerceString(record.form?.brief),
    sourceText: coerceString(record.form?.sourceText),
    context: coerceString(record.form?.context),
    audience: coerceString(record.form?.audience),
    tone: coerceString(record.form?.tone),
    outputFormat: coerceString(record.form?.outputFormat),
    extraInstructions: coerceString(record.form?.extraInstructions),
  } satisfies RuntimeFormState;

  const creditCost = Number(record.creditCost);

  return {
    id,
    title,
    toolSlug: coerceString(record.toolSlug) as ToolEntry['slug'],
    createdAt,
    updatedAt,
    presetId: coerceString(record.presetId) || null,
    form,
    outputText,
    model: coerceString(record.model) || null,
    finishReason: coerceString(record.finishReason) || null,
    requestId: coerceString(record.requestId) || null,
    creditCost: Number.isFinite(creditCost) ? Math.max(0, Math.trunc(creditCost)) : null,
    isSaved: Number(record.isSaved ?? 0) === 1 || record.isSaved === true,
    usage: coerceUsage(record.usage),
  };
}

function normalizeApiRunRecord(value: unknown): StoredRunRecord | null {
  if (!value || typeof value !== 'object') {
    return null;
  }

  const record = value as {
    id?: unknown;
    title?: unknown;
    toolSlug?: unknown;
    presetId?: unknown;
    brief?: unknown;
    sourceText?: unknown;
    context?: unknown;
    audience?: unknown;
    tone?: unknown;
    outputFormat?: unknown;
    extraInstructions?: unknown;
    outputText?: unknown;
    model?: unknown;
    finishReason?: unknown;
    requestId?: unknown;
    aiCreditsUsed?: unknown;
    isSaved?: unknown;
    createdAt?: unknown;
    updatedAt?: unknown;
    promptTokens?: unknown;
    completionTokens?: unknown;
    totalTokens?: unknown;
    costUsd?: unknown;
  };

  const id = coerceString(record.id);
  const title = coerceString(record.title);
  const createdAt = coerceString(record.createdAt);
  const outputText = coerceString(record.outputText);

  if (!id || !title || !createdAt || !outputText) {
    return null;
  }

  const promptTokens = Number(record.promptTokens);
  const completionTokens = Number(record.completionTokens);
  const totalTokens = Number(record.totalTokens);
  const costUsd = Number(record.costUsd);

  return {
    id,
    title,
    toolSlug: coerceString(record.toolSlug) as ToolEntry['slug'],
    createdAt,
    updatedAt: coerceString(record.updatedAt) || createdAt,
    presetId: coerceString(record.presetId) || null,
    form: {
      brief: coerceString(record.brief),
      sourceText: coerceString(record.sourceText),
      context: coerceString(record.context),
      audience: coerceString(record.audience),
      tone: coerceString(record.tone),
      outputFormat: coerceString(record.outputFormat),
      extraInstructions: coerceString(record.extraInstructions),
    },
    outputText,
    model: coerceString(record.model) || null,
    finishReason: coerceString(record.finishReason) || null,
    requestId: coerceString(record.requestId) || null,
    creditCost: Number.isFinite(Number(record.aiCreditsUsed))
      ? Math.max(0, Math.trunc(Number(record.aiCreditsUsed)))
      : null,
    isSaved: Boolean(record.isSaved),
    usage:
      Number.isFinite(promptTokens) &&
      Number.isFinite(completionTokens) &&
      Number.isFinite(totalTokens) &&
      Number.isFinite(costUsd)
        ? {
            promptTokens: Math.max(0, Math.trunc(promptTokens)),
            completionTokens: Math.max(0, Math.trunc(completionTokens)),
            totalTokens: Math.max(0, Math.trunc(totalTokens)),
            costUsd: Math.max(0, costUsd),
          }
        : null,
  };
}

function readStoredRunRecords(storageKey: string | null, limit: number) {
  if (!storageKey || typeof window === 'undefined') {
    return [] as StoredRunRecord[];
  }

  try {
    const raw = window.localStorage.getItem(storageKey);

    if (!raw) {
      return [];
    }

    const parsed = JSON.parse(raw) as unknown;

    if (!Array.isArray(parsed)) {
      return [];
    }

    return parsed
      .map(normalizeStoredRunRecord)
      .filter((record): record is StoredRunRecord => Boolean(record))
      .slice(0, limit);
  } catch {
    return [];
  }
}

function writeStoredRunRecords(storageKey: string | null, records: StoredRunRecord[]) {
  if (!storageKey || typeof window === 'undefined') {
    return;
  }

  try {
    if (!records.length) {
      window.localStorage.removeItem(storageKey);
      return;
    }

    window.localStorage.setItem(storageKey, JSON.stringify(records));
  } catch {
    return;
  }
}

function buildRunTitle(brief: string, presetTitle: string | null) {
  if (presetTitle) {
    return presetTitle;
  }

  const normalized = brief.replace(/\s+/g, ' ').trim();

  if (!normalized) {
    return 'Untitled run';
  }

  return normalized.length > 72 ? `${normalized.slice(0, 69)}...` : normalized;
}

function createDefaultFormState(config: RuntimeConfig): RuntimeFormState {
  const next = { ...emptyFormState };

  for (const key of config.fieldOrder) {
    const field = config.fields[key];

    if (field.kind === 'select') {
      next[key] = field.defaultValue ?? field.options?.[0]?.value ?? '';
      continue;
    }

    next[key] = field.defaultValue ?? '';
  }

  return next;
}

function formatRunTimestamp(value: string) {
  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return 'Just now';
  }

  return new Intl.DateTimeFormat('en', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(date);
}

function getRuntimeConfig(entry: ToolEntry) {
  return (
    runtimeConfigBySlug[entry.slug] ??
    createRuntimeConfig({
      fieldOrder: ['brief', 'sourceText', 'audience', 'tone', 'outputFormat', 'extraInstructions'],
    })
  );
}

function isFullWidthField(key: RuntimeFieldKey, field: RuntimeFieldDefinition) {
  return field.kind === 'textarea' || key === 'brief' || key === 'sourceText' || key === 'context';
}

function AiWorkflowRuntimePanelInner({ entry }: AiWorkflowRuntimePanelProps) {
  const { user, session, isLoading, isSessionLoading, refreshSession } = useFirebaseAuth();
  const config = useMemo(() => getRuntimeConfig(entry), [entry]);
  const taskPresets = useMemo(() => getTaskPresetsForTool(entry.slug), [entry.slug]);
  const defaultTaskPreset = useMemo(() => getDefaultTaskPresetForTool(entry.slug), [entry.slug]);
  const defaultFormState = useMemo(() => createDefaultFormState(config), [config]);
  const [formState, setFormState] = useState<RuntimeFormState>(defaultFormState);
  const [previewPresetId, setPreviewPresetId] = useState<string | null>(
    defaultTaskPreset?.id ?? null,
  );
  const [selectedPresetId, setSelectedPresetId] = useState<string | null>(null);
  const [currentRunRecord, setCurrentRunRecord] = useState<StoredRunRecord | null>(null);
  const [historyRecords, setHistoryRecords] = useState<StoredRunRecord[]>([]);
  const [savedRecords, setSavedRecords] = useState<StoredRunRecord[]>([]);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [copyState, setCopyState] = useState<'idle' | 'copied'>('idle');
  const [outputView, setOutputView] = useState<'formatted' | 'raw'>('formatted');
  const [isRunsLoading, setIsRunsLoading] = useState(false);
  const [isRunning, setIsRunning] = useState(false);

  const isSignedIn = Boolean(user);
  const canUseAi = Boolean(session?.account?.canUseAi);
  const nextPath = `/tools/${entry.slug}`;
  const signupHref = `/signup?next=${encodeURIComponent(nextPath)}`;
  const accountHref = `/account?next=${encodeURIComponent(nextPath)}`;
  const previewPreset = getTaskPresetById(entry.slug, previewPresetId) ?? defaultTaskPreset;
  const historyStorageKey = useMemo(
    () => (user ? getLocalRuntimeStorageKey('history', entry.slug, user) : null),
    [entry.slug, user],
  );
  const savedStorageKey = useMemo(
    () => (user ? getLocalRuntimeStorageKey('saved', entry.slug, user) : null),
    [entry.slug, user],
  );
  const usageSummary = currentRunRecord?.usage ?? null;
  const formattedOutputHtml = useMemo(
    () => renderRuntimeMarkdownToHtml(currentRunRecord?.outputText ?? ''),
    [currentRunRecord?.outputText],
  );
  const structuredOutputSections = useMemo(
    () =>
      extractStructuredOutputSections(
        currentRunRecord?.outputText ?? '',
        config.outputSections ?? [],
      ).map((section) => ({
        ...section,
        html: renderRuntimeMarkdownToHtml(section.markdown),
      })),
    [config.outputSections, currentRunRecord?.outputText],
  );
  const currentRunIsSaved = currentRunRecord
    ? savedRecords.some((record) => record.id === currentRunRecord.id)
    : false;

  useEffect(() => {
    setFormState(defaultFormState);
    setPreviewPresetId(defaultTaskPreset?.id ?? null);
    setSelectedPresetId(null);
    setCurrentRunRecord(null);
    setErrorMessage(null);
    setCopyState('idle');
    setOutputView('formatted');
  }, [defaultFormState, defaultTaskPreset?.id, entry.slug]);

  useEffect(() => {
    if (!user || !canUseAi) {
      setHistoryRecords(readStoredRunRecords(historyStorageKey, AI_RUNTIME_HISTORY_LIMIT));
      setSavedRecords(readStoredRunRecords(savedStorageKey, AI_RUNTIME_SAVED_LIMIT));
      return;
    }

    const currentUser = user;
    let cancelled = false;

    async function loadServerRuns() {
      setIsRunsLoading(true);

      try {
        const idToken = await currentUser.getIdToken();
        const response = await fetch(`/api/ai/runs?toolSlug=${encodeURIComponent(entry.slug)}`, {
          headers: {
            authorization: `Bearer ${idToken}`,
          },
        });
        const payload = (await response.json().catch(() => ({}))) as AiRunsPayload;

        if (!response.ok || !payload.success || !payload.data) {
          throw new Error(getApiErrorMessage(payload.error));
        }

        const recent = Array.isArray(payload.data.recent)
          ? payload.data.recent
              .map(normalizeApiRunRecord)
              .filter((record): record is StoredRunRecord => Boolean(record))
              .slice(0, AI_RUNTIME_HISTORY_LIMIT)
          : [];
        const saved = Array.isArray(payload.data.saved)
          ? payload.data.saved
              .map(normalizeApiRunRecord)
              .filter((record): record is StoredRunRecord => Boolean(record))
              .slice(0, AI_RUNTIME_SAVED_LIMIT)
          : [];

        if (cancelled) {
          return;
        }

        replaceHistoryRecords(recent);
        replaceSavedRecords(saved);
      } catch {
        if (cancelled) {
          return;
        }

        setHistoryRecords(readStoredRunRecords(historyStorageKey, AI_RUNTIME_HISTORY_LIMIT));
        setSavedRecords(readStoredRunRecords(savedStorageKey, AI_RUNTIME_SAVED_LIMIT));
      } finally {
        if (!cancelled) {
          setIsRunsLoading(false);
        }
      }
    }

    void loadServerRuns();

    return () => {
      cancelled = true;
    };
  }, [canUseAi, entry.slug, historyStorageKey, savedStorageKey, user]);

  function replaceHistoryRecords(nextRecords: StoredRunRecord[]) {
    setHistoryRecords(nextRecords);
    writeStoredRunRecords(historyStorageKey, nextRecords);
  }

  function replaceSavedRecords(nextRecords: StoredRunRecord[]) {
    setSavedRecords(nextRecords);
    writeStoredRunRecords(savedStorageKey, nextRecords);
  }

  function updateField(key: RuntimeFieldKey, value: string) {
    setFormState((current) => ({
      ...current,
      [key]: value,
    }));
  }

  function applyPreset(preset: AiWorkflowTaskPreset) {
    setFormState({
      ...defaultFormState,
      ...preset.values,
    });
    setPreviewPresetId(preset.id);
    setSelectedPresetId(preset.id);
    setErrorMessage(null);
  }

  function restoreRecord(record: StoredRunRecord) {
    setFormState({
      ...defaultFormState,
      ...record.form,
    });
    setPreviewPresetId(record.presetId || defaultTaskPreset?.id || null);
    setSelectedPresetId(record.presetId);
    setCurrentRunRecord(record);
    setErrorMessage(null);
    setCopyState('idle');
    setOutputView('formatted');
  }

  async function toggleSaveRecord(record: StoredRunRecord) {
    const alreadySaved = savedRecords.some((item) => item.id === record.id);

    if (user) {
      try {
        const idToken = await user.getIdToken();
        const response = await fetch('/api/ai/runs', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            authorization: `Bearer ${idToken}`,
          },
          body: JSON.stringify({
            action: alreadySaved ? 'unsave' : 'save',
            toolSlug: entry.slug,
            runId: record.id,
          }),
        });
        const payload = (await response.json().catch(() => ({}))) as AiRunsPayload;

        if (!response.ok || !payload.success || !payload.data) {
          throw new Error(getApiErrorMessage(payload.error));
        }

        const recent = Array.isArray(payload.data.recent)
          ? payload.data.recent
              .map(normalizeApiRunRecord)
              .filter((item): item is StoredRunRecord => Boolean(item))
              .slice(0, AI_RUNTIME_HISTORY_LIMIT)
          : [];
        const saved = Array.isArray(payload.data.saved)
          ? payload.data.saved
              .map(normalizeApiRunRecord)
              .filter((item): item is StoredRunRecord => Boolean(item))
              .slice(0, AI_RUNTIME_SAVED_LIMIT)
          : [];

        replaceHistoryRecords(recent);
        replaceSavedRecords(saved);

        if (currentRunRecord?.id === record.id) {
          const nextCurrent =
            recent.find((item) => item.id === record.id) ??
            saved.find((item) => item.id === record.id);
          setCurrentRunRecord(nextCurrent ?? currentRunRecord);
        }

        return;
      } catch (error) {
        setErrorMessage(getApiErrorMessage(error));
        return;
      }
    }

    if (alreadySaved) {
      replaceSavedRecords(savedRecords.filter((item) => item.id !== record.id));
      if (currentRunRecord?.id === record.id) {
        setCurrentRunRecord({ ...record, isSaved: false });
      }
      return;
    }

    replaceSavedRecords(
      [{ ...record, isSaved: true }, ...savedRecords].slice(0, AI_RUNTIME_SAVED_LIMIT),
    );
    if (currentRunRecord?.id === record.id) {
      setCurrentRunRecord({ ...record, isSaved: true });
    }
  }

  async function handleRun() {
    if (!user) {
      setErrorMessage('Sign in is required to run AI workflows.');
      return;
    }

    if (!canUseAi) {
      setErrorMessage('Sign in with a Cleanor account to run this workflow.');
      return;
    }

    if (!formState.brief.trim()) {
      setErrorMessage('Add a short workflow brief before running.');
      return;
    }

    setIsRunning(true);
    setErrorMessage(null);
    setCopyState('idle');

    try {
      const idToken = await user.getIdToken();
      const response = await fetch('/api/ai/run', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify({
          toolSlug: entry.slug,
          presetId: selectedPresetId,
          brief: formState.brief,
          sourceText: formState.sourceText,
          context: formState.context,
          audience: formState.audience,
          tone: formState.tone,
          outputFormat: formState.outputFormat,
          extraInstructions: formState.extraInstructions,
        }),
      });
      const payload = (await response.json().catch(() => ({}))) as AiRunPayload;

      if (!response.ok || !payload.success || !payload.data?.output?.text) {
        throw new Error(getApiErrorMessage(payload.error));
      }

      const nextRecord =
        normalizeApiRunRecord(payload.data.run) ??
        ({
          id: payload.data.requestId ?? `run-${Date.now()}`,
          title: buildRunTitle(
            formState.brief,
            getTaskPresetById(entry.slug, selectedPresetId)?.title ??
              payload.data.preset?.title ??
              null,
          ),
          toolSlug: entry.slug,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          presetId: selectedPresetId,
          form: { ...formState },
          outputText: payload.data.output.text,
          model: payload.data.model ?? null,
          finishReason: payload.data.output.finishReason ?? null,
          requestId: payload.data.requestId ?? null,
          creditCost:
            typeof payload.data.preset?.creditCost === 'number'
              ? payload.data.preset.creditCost
              : null,
          isSaved: false,
          usage: payload.data.usage
            ? {
                promptTokens: Math.max(0, Math.trunc(payload.data.usage.promptTokens ?? 0)),
                completionTokens: Math.max(0, Math.trunc(payload.data.usage.completionTokens ?? 0)),
                totalTokens: Math.max(0, Math.trunc(payload.data.usage.totalTokens ?? 0)),
                costUsd: Math.max(0, payload.data.usage.costUsd ?? 0),
              }
            : null,
        } satisfies StoredRunRecord);

      setCurrentRunRecord(nextRecord);
      setOutputView('formatted');
      replaceHistoryRecords(
        [nextRecord, ...historyRecords.filter((item) => item.id !== nextRecord.id)].slice(
          0,
          AI_RUNTIME_HISTORY_LIMIT,
        ),
      );
      await refreshSession();
    } catch (error) {
      setErrorMessage(getApiErrorMessage(error));
    } finally {
      setIsRunning(false);
    }
  }

  async function handleCopy() {
    if (!currentRunRecord?.outputText) {
      return;
    }

    try {
      await navigator.clipboard.writeText(currentRunRecord.outputText);
      setCopyState('copied');
      window.setTimeout(() => setCopyState('idle'), 1800);
    } catch {
      setErrorMessage('Could not copy the output. Try selecting it manually.');
    }
  }

  function handleReuseOutputAsSource() {
    if (!currentRunRecord?.outputText) {
      return;
    }

    updateField('sourceText', currentRunRecord.outputText);
    setErrorMessage(null);
  }

  function handleReset() {
    setFormState(defaultFormState);
    setPreviewPresetId(defaultTaskPreset?.id ?? null);
    setSelectedPresetId(null);
    setCurrentRunRecord(null);
    setErrorMessage(null);
    setCopyState('idle');
  }

  async function handleClearHistory() {
    if (!user) {
      replaceHistoryRecords([]);
      return;
    }

    try {
      const idToken = await user.getIdToken();
      const response = await fetch('/api/ai/runs', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify({
          action: 'clearHistory',
          toolSlug: entry.slug,
        }),
      });
      const payload = (await response.json().catch(() => ({}))) as AiRunsPayload;

      if (!response.ok || !payload.success || !payload.data) {
        throw new Error(getApiErrorMessage(payload.error));
      }

      const recent = Array.isArray(payload.data.recent)
        ? payload.data.recent
            .map(normalizeApiRunRecord)
            .filter((record): record is StoredRunRecord => Boolean(record))
            .slice(0, AI_RUNTIME_HISTORY_LIMIT)
        : [];
      const saved = Array.isArray(payload.data.saved)
        ? payload.data.saved
            .map(normalizeApiRunRecord)
            .filter((record): record is StoredRunRecord => Boolean(record))
            .slice(0, AI_RUNTIME_SAVED_LIMIT)
        : [];

      replaceHistoryRecords(recent);
      replaceSavedRecords(saved);
      if (currentRunRecord && !currentRunRecord.isSaved) {
        const nextCurrent =
          recent.find((record) => record.id === currentRunRecord.id) ??
          saved.find((record) => record.id === currentRunRecord.id) ??
          null;
        setCurrentRunRecord(nextCurrent);
      }
    } catch (error) {
      setErrorMessage(getApiErrorMessage(error));
    }
  }

  function renderField(key: RuntimeFieldKey) {
    const field = config.fields[key];
    const isFullWidth = isFullWidthField(key, field);
    const className = isFullWidth ? 'tool-live-field tool-live-field-full' : 'tool-live-field';

    if (field.kind === 'select') {
      return (
        <label className={className} key={key}>
          <span>{field.label}</span>
          <select value={formState[key]} onChange={(event) => updateField(key, event.target.value)}>
            {(field.options ?? []).map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          {field.help ? <small>{field.help}</small> : null}
        </label>
      );
    }

    if (field.kind === 'textarea') {
      return (
        <label className={className} key={key}>
          <span>{field.label}</span>
          <textarea
            placeholder={field.placeholder}
            rows={field.rows}
            value={formState[key]}
            onChange={(event) => updateField(key, event.target.value)}
          />
          {field.help ? <small>{field.help}</small> : null}
        </label>
      );
    }

    return (
      <label className={className} key={key}>
        <span>{field.label}</span>
        <input
          placeholder={field.placeholder}
          value={formState[key]}
          onChange={(event) => updateField(key, event.target.value)}
        />
        {field.help ? <small>{field.help}</small> : null}
      </label>
    );
  }

  return (
    <article className="project-detail-card discovery-detail-card tool-ai-runtime">
      <div className="tool-ai-placeholder-top">
        <div className="article-meta-strip discovery-meta-strip">
          <span>Live beta</span>
          <span>Free with a signed-in account</span>
        </div>
        <div className="tool-ai-placeholder-status">
          <strong>{entry.cardTitle}</strong>
          <span>{entry.cardDescription}</span>
        </div>
      </div>

      <div className="tool-ai-runtime-grid">
        <div className="tool-ai-runtime-main">
          {isLoading || isSessionLoading ? (
            <div className="tool-ai-runtime-message">
              <strong>Checking account access</strong>
              <p>Loading your current AI access state and credit balance.</p>
            </div>
          ) : !isSignedIn ? (
            <div className="tool-ai-runtime-message">
              <strong>Sign in to run this workflow</strong>
              <p>
                The page stays public, but the runtime attaches to a signed-in account. AI workflows
                are free with a Cleanor account — just sign in to run them.
              </p>
              <div className="hero-actions">
                <Link className="button" href={signupHref}>
                  Create free account
                </Link>
              </div>
            </div>
          ) : !canUseAi ? (
            <div className="tool-ai-runtime-message">
              <strong>Sign in to run AI workflows</strong>
              <p>AI workflows are free with a Cleanor account. Open your account to continue.</p>
              <div className="hero-actions">
                <Link className="button" href={accountHref}>
                  Open account
                </Link>
              </div>
            </div>
          ) : (
            <>
              <div className="tool-ai-runtime-note">
                <strong>AI access active — free with your account</strong>
                <span>
                  Task presets fill recommended fields without auto-running the workflow, and each
                  run stays tied to server-backed history.
                </span>
              </div>

              {taskPresets.length ? (
                <section className="tool-ai-runtime-panel tool-ai-runtime-presets-panel">
                  <div className="tool-ai-runtime-section-heading">
                    <strong>Choose a task</strong>
                    <span>
                      Pick a common specialist task, preview the recommended setup, and apply it
                      only when you want to overwrite the form.
                    </span>
                  </div>
                  <label className="tool-live-field tool-live-field-full">
                    <span>Task</span>
                    <select
                      value={previewPreset?.id ?? ''}
                      onChange={(event) => setPreviewPresetId(event.target.value || null)}
                    >
                      {taskPresets.map((preset) => (
                        <option key={preset.id} value={preset.id}>
                          {preset.title}
                        </option>
                      ))}
                    </select>
                    <small>
                      Choose the task template first. The form changes only after you apply it.
                    </small>
                  </label>
                  {previewPreset ? (
                    <article className="tool-ai-runtime-preset-preview">
                      <div className="tool-ai-runtime-preset-preview-copy">
                        <strong>{previewPreset.title}</strong>
                        <p>{previewPreset.description}</p>
                        <span>{previewPreset.recommendedFor}</span>
                      </div>
                      <div className="tool-ai-runtime-preset-preview-meta">
                        <div className="tool-ai-runtime-preset-chips">
                          {previewPreset.tags.map((tag) => (
                            <span className="tool-ai-runtime-preset-chip" key={tag}>
                              {tag}
                            </span>
                          ))}
                        </div>
                        <p>
                          Fields included:{' '}
                          {getTaskPresetFieldLabels(previewPreset.values).join(', ')}
                        </p>
                        <p>Editable token: {previewPreset.starterToken}</p>
                        <div className="project-card-actions">
                          <button
                            className="button button-ghost button-ghost-light"
                            onClick={() => applyPreset(previewPreset)}
                            type="button"
                          >
                            {selectedPresetId === previewPreset.id ? 'Applied' : 'Apply preset'}
                          </button>
                        </div>
                      </div>
                    </article>
                  ) : null}
                </section>
              ) : null}

              <section className="tool-ai-runtime-panel">
                <div className="tool-ai-runtime-section-heading">
                  <strong>Workflow input</strong>
                  <span>
                    Fields are tuned to this workflow, but you can still shape the run manually.
                  </span>
                </div>

                <div className="tool-form-field-list">
                  {config.fieldOrder.map((key) => renderField(key))}
                </div>
              </section>

              {errorMessage ? (
                <div className="tool-ai-runtime-error" role="alert">
                  {errorMessage}
                </div>
              ) : null}

              <div className="hero-actions tool-ai-runtime-actions">
                <button
                  className="button"
                  disabled={isRunning}
                  onClick={() => void handleRun()}
                  type="button"
                >
                  {isRunning ? 'Running workflow...' : 'Run workflow'}
                </button>
                <button
                  className="button button-ghost button-ghost-light"
                  onClick={handleReset}
                  type="button"
                >
                  Reset
                </button>
                {currentRunRecord ? (
                  <button
                    className="button button-ghost button-ghost-light"
                    onClick={() => toggleSaveRecord(currentRunRecord)}
                    type="button"
                  >
                    {currentRunIsSaved ? 'Unsave run' : 'Save run'}
                  </button>
                ) : null}
              </div>

              <section className="tool-ai-runtime-panel tool-ai-runtime-output-shell">
                <div className="tool-ai-runtime-output-header">
                  <div className="tool-ai-runtime-section-heading">
                    <strong>{config.outputHeading}</strong>
                    <span>
                      {currentRunRecord
                        ? `Last updated ${formatRunTimestamp(currentRunRecord.updatedAt)}`
                        : config.outputEmptyState}
                    </span>
                  </div>
                  {currentRunRecord ? (
                    <div className="tool-ai-runtime-output-actions">
                      <button
                        className={`button button-ghost button-ghost-light${
                          outputView === 'formatted' ? ' tool-ai-runtime-view-button-active' : ''
                        }`}
                        onClick={() => setOutputView('formatted')}
                        type="button"
                      >
                        Formatted
                      </button>
                      <button
                        className={`button button-ghost button-ghost-light${
                          outputView === 'raw' ? ' tool-ai-runtime-view-button-active' : ''
                        }`}
                        onClick={() => setOutputView('raw')}
                        type="button"
                      >
                        Raw
                      </button>
                      <button
                        className="button button-ghost button-ghost-light"
                        onClick={() => void handleCopy()}
                        type="button"
                      >
                        {copyState === 'copied' ? 'Copied' : 'Copy output'}
                      </button>
                      <button
                        className="button button-ghost button-ghost-light"
                        onClick={handleReuseOutputAsSource}
                        type="button"
                      >
                        Reuse as source
                      </button>
                    </div>
                  ) : null}
                </div>

                {currentRunRecord ? (
                  <>
                    <div className="tool-ai-runtime-usage">
                      <span>{currentRunRecord.model ?? 'Preset model'}</span>
                      {usageSummary ? (
                        <>
                          <span>{usageSummary.promptTokens} prompt</span>
                          <span>{usageSummary.completionTokens} completion</span>
                          <span>{usageSummary.totalTokens} total</span>
                          <span>${usageSummary.costUsd.toFixed(4)}</span>
                        </>
                      ) : null}
                      {currentRunRecord.creditCost !== null ? (
                        <span>{currentRunRecord.creditCost} AI credits</span>
                      ) : null}
                    </div>
                    {outputView === 'formatted' && formattedOutputHtml ? (
                      structuredOutputSections.length >= 2 ? (
                        <div className="tool-ai-runtime-output-sections">
                          {structuredOutputSections.map((section) => (
                            <section className="tool-ai-runtime-output-section" key={section.id}>
                              <div className="tool-ai-runtime-output-section-top">
                                <strong>{section.title}</strong>
                                <span>{section.description}</span>
                              </div>
                              <div
                                className="discovery-prose tool-ai-runtime-output-body tool-ai-runtime-output-prose tool-ai-runtime-output-section-body"
                                dangerouslySetInnerHTML={{ __html: section.html }}
                              />
                            </section>
                          ))}
                        </div>
                      ) : (
                        <div
                          className="discovery-prose tool-ai-runtime-output-body tool-ai-runtime-output-prose"
                          dangerouslySetInnerHTML={{ __html: formattedOutputHtml }}
                        />
                      )
                    ) : (
                      <pre className="tool-ai-runtime-output-body">
                        {currentRunRecord.outputText}
                      </pre>
                    )}
                  </>
                ) : (
                  <div className="tool-ai-runtime-output-empty">
                    <strong>No output yet</strong>
                    <p>{config.outputEmptyState}</p>
                  </div>
                )}
              </section>
            </>
          )}
        </div>

        <aside className="tool-ai-runtime-sidebar">
          <article className="overview-card tool-ai-placeholder-card">
            <h3>Inputs</h3>
            <ul className="detail-list detail-list-compact">
              {(entry.primaryInputs ?? []).map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </article>
          <article className="overview-card tool-ai-placeholder-card">
            <h3>Outputs</h3>
            <ul className="detail-list detail-list-compact">
              {(entry.outputs ?? []).map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </article>
          <article className="overview-card tool-ai-placeholder-card">
            <h3>Capabilities</h3>
            <ul className="detail-list detail-list-compact">
              {(entry.capabilities ?? []).map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </article>

          <article className="overview-card tool-ai-runtime-sidebar-section">
            <div className="tool-ai-runtime-section-heading">
              <strong>Saved runs</strong>
              <span>Keep strong outputs pinned on your account for this workflow.</span>
            </div>
            {isRunsLoading ? (
              <p className="tool-ai-runtime-sidebar-empty">Loading saved runs...</p>
            ) : savedRecords.length ? (
              <div className="tool-ai-runtime-card-list">
                {savedRecords.map((record) => (
                  <article className="tool-ai-runtime-history-card" key={record.id}>
                    <div className="tool-ai-runtime-card-copy">
                      <strong>{record.title}</strong>
                      {record.presetId ? (
                        <span>
                          Task preset ·{' '}
                          {getTaskPresetById(entry.slug, record.presetId)?.title ?? record.presetId}
                        </span>
                      ) : null}
                      <span>{formatRunTimestamp(record.createdAt)}</span>
                      <p>{record.outputText}</p>
                    </div>
                    <div className="tool-ai-runtime-card-actions">
                      <button
                        className="soft-link"
                        onClick={() => restoreRecord(record)}
                        type="button"
                      >
                        Restore
                      </button>
                      <button
                        className="soft-link"
                        onClick={() => void toggleSaveRecord(record)}
                        type="button"
                      >
                        Remove
                      </button>
                    </div>
                  </article>
                ))}
              </div>
            ) : (
              <p className="tool-ai-runtime-sidebar-empty">
                Save a run after the first successful output to keep it on hand.
              </p>
            )}
          </article>

          <article className="overview-card tool-ai-runtime-sidebar-section">
            <div className="tool-ai-runtime-section-heading">
              <strong>Recent runs</strong>
              <span>Restore a recent run without spending credits again.</span>
            </div>
            {isRunsLoading ? (
              <p className="tool-ai-runtime-sidebar-empty">Loading run history...</p>
            ) : historyRecords.length ? (
              <>
                <div className="tool-ai-runtime-card-list">
                  {historyRecords.map((record) => (
                    <article className="tool-ai-runtime-history-card" key={record.id}>
                      <div className="tool-ai-runtime-card-copy">
                        <strong>{record.title}</strong>
                        {record.presetId ? (
                          <span>
                            Task preset ·{' '}
                            {getTaskPresetById(entry.slug, record.presetId)?.title ??
                              record.presetId}
                          </span>
                        ) : null}
                        <span>{formatRunTimestamp(record.createdAt)}</span>
                        <p>{record.form.brief || record.outputText}</p>
                      </div>
                      <div className="tool-ai-runtime-card-actions">
                        <button
                          className="soft-link"
                          onClick={() => restoreRecord(record)}
                          type="button"
                        >
                          Restore
                        </button>
                        <button
                          className="soft-link"
                          onClick={() => void toggleSaveRecord(record)}
                          type="button"
                        >
                          {savedRecords.some((item) => item.id === record.id) ? 'Saved' : 'Save'}
                        </button>
                      </div>
                    </article>
                  ))}
                </div>
                <button
                  className="button button-ghost button-ghost-light"
                  onClick={() => void handleClearHistory()}
                  type="button"
                >
                  Clear history
                </button>
              </>
            ) : (
              <p className="tool-ai-runtime-sidebar-empty">
                Your run history will appear here after the first completed run.
              </p>
            )}
          </article>
        </aside>
      </div>
    </article>
  );
}

export default function AiWorkflowRuntimePanel(props: AiWorkflowRuntimePanelProps) {
  return <AiWorkflowRuntimePanelInner {...props} />;
}
